// Shared detection + self-heal logic for stale-chunk errors after a frontend deploy
// replaces hashed asset filenames out from under an already-open tab. Used by both
// ErrorBoundary.tsx (catches failures inside the React render tree — lazy-loaded
// routes) and main.tsx's global listeners (catches failures OUTSIDE the tree, e.g. a
// vendor chunk like posthog-js failing to load before the app even mounts, which no
// React error boundary can ever see). Both call `tryReloadOnce` so they share one
// cooldown key and never double-reload.
//
// `lazyImport` (below) normalizes what a failed dynamic import() rejects with, since
// that shape isn't consistent across browsers — e.g. Safari has been observed
// rejecting with a bare object carrying no `message`/`name` at all, which the string/
// name matching below can never recognize. Normalizing at the import site means every
// lazy route reports a real `ChunkLoadError` regardless of the browser, so the
// isChunkLoadError checks downstream don't need to keep growing new browser-specific
// cases.

import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_COOLDOWN_MS = 10000;
const RELOAD_STORAGE_KEY = "last-chunk-reload";

export function isChunkLoadError(message: string | null | undefined, name?: string | null): boolean {
  if (name === "ChunkLoadError") return true;
  if (!message) return false;
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("is not a valid JavaScript MIME type") ||
    message.includes("Importing a module script failed") ||
    // Real Chrome phrasing observed in production for a script-tag (not dynamic
    // import()) load failure — distinct from the "is not a valid JavaScript MIME
    // type" variant above, and previously unmatched by either check.
    message.includes("Failed to load module script")
  );
}

/**
 * Converts any dynamic import() rejection reason into a proper Error named
 * "ChunkLoadError", regardless of what the browser actually rejected with (Safari has
 * been observed rejecting with a bare object carrying no message/name at all).
 */
export function normalizeChunkError(reason: unknown): Error {
  const message =
    reason instanceof Error && reason.message
      ? reason.message
      : "Failed to fetch dynamically imported module";
  const chunkError = new Error(message);
  chunkError.name = "ChunkLoadError";
  return chunkError;
}

/**
 * Drop-in replacement for React.lazy(() => import(...)) that runs every import
 * failure through normalizeChunkError first. Keeps isChunkLoadError() as the single
 * source of truth for recognizing these failures instead of every call site having to
 * match on browser-specific rejection shapes.
 */
export function lazyImport<P>(
  factory: () => Promise<{ default: ComponentType<P> }>,
): LazyExoticComponent<ComponentType<P>> {
  return lazy(() =>
    factory().catch((reason: unknown) => {
      throw normalizeChunkError(reason);
    }),
  );
}

/**
 * Reloads the page at most once per RELOAD_COOLDOWN_MS window. Returns true if this
 * call actually triggered (or a very recent call already triggered) a reload, so the
 * caller can suppress error reporting / show a neutral loading state instead of an
 * error fallback.
 */
export function tryReloadOnce(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_STORAGE_KEY);
  const withinCooldown = !!lastReload && Date.now() - parseInt(lastReload, 10) < RELOAD_COOLDOWN_MS;

  if (!withinCooldown) {
    sessionStorage.setItem(RELOAD_STORAGE_KEY, Date.now().toString());
    window.location.reload();
  }

  return true;
}
